// apps/web/src/api/agent/stream.ts
//
// Bearer-authenticated SSE consumer for the analyst agent run endpoint
// (#1876). The app has no streaming primitive today, so this is the
// load-bearing new capability: a `fetch`-based POST + ReadableStream reader.
// `EventSource` is intentionally NOT used — it cannot attach the in-memory
// Bearer access token to its request.
//
// The auth-retry contract mirrors `request<T>` / `fetchBinaryWithAuthRetry`
// in `../client.ts`: capture the token at request start, and on a `401`
// call `coordinatedRefresh()` ONCE and re-POST from scratch (no mid-body
// resume — a half-consumed SSE body cannot be rewound). Drift from that
// contract opens a hole where the JSON path silently refreshes but the
// stream fails on a stale 401.
import { AgentV2EventSchema, type AgentV2AskRequest, type AgentV2Event } from '@magimp/shared';
import { useAuthStore } from '../../stores/auth.js';
import { ApiClientError, NetworkError, parseApiError } from '../errors.js';
import { coordinatedRefresh } from '../refresh.js';
import { clearWithBroadcast, type SessionLostReason } from '../authEvents.js';

// WHY: this path is fixed by the issue's verified integration point
// (`POST /api/dashboards/agent/v2/ask`). The `/api` prefix is added here
// because this helper does not route through `buildUrl` in client.ts.
const AGENT_ASK_PATH = '/api/dashboards/agent/v2/ask';

async function postAsk(
  body: AgentV2AskRequest,
  token: string | null,
  signal: AbortSignal,
): Promise<Response> {
  const headers = new Headers({ 'content-type': 'application/json' });
  if (token) headers.set('authorization', `Bearer ${token}`);
  try {
    return await fetch(AGENT_ASK_PATH, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    throw new NetworkError('Network request failed', { cause: e });
  }
}

// Add a local reason mapping mirroring the private one in client.ts. The
// auth-retry block below is a deliberate faithful duplicate of
// fetchBinaryWithAuthRetry per client.ts's documented lock-step invariant.
function refreshErrorReason(err: ApiClientError): SessionLostReason {
  if (err.code === 'AUTH_REFRESH_INVALID') return 'AUTH_REFRESH_INVALID';
  if (err.code === 'AUTH_REFRESH_REUSED') return 'AUTH_REFRESH_REUSED';
  return 'AUTH_REFRESH_4XX';
}

/**
 * @aiPurpose POST the ask request and return the raw, unconsumed streaming
 *   `Response`. On a `401` with a token present, faithfully mirror
 *   `fetchBinaryWithAuthRetry` in `../client.ts`: short-circuit to a retry
 *   when a concurrent request already rotated the store token, otherwise
 *   refresh ONCE via `coordinatedRefresh()` and re-POST from scratch.
 *   Non-2xx responses are surfaced through the standard `ApiClientError` /
 *   `NetworkError` channels so caller error handling stays uniform.
 * @aiInvariant single refresh + single retry — never a retry loop. A stale
 *   401 (the store already holds a rotated token) retries WITHOUT burning a
 *   second refresh. A `NetworkError` from refresh (5xx / throttle /
 *   session-changed) stays transient and preserves the session. An
 *   authoritative refresh loss (4xx from `/api/auth/refresh`) clears the
 *   session via `clearWithBroadcast` and throws `AUTH_TOKEN_INVALID`. The
 *   body is left unread so the caller can pull `response.body.getReader()`.
 *   The precedent's `skipAuthRetry` escape hatch is intentionally NOT ported
 *   — the agent stream has no bootstrap caller that needs it.
 * @throws ApiClientError on a non-2xx JSON-envelope response, or
 *   `AUTH_TOKEN_INVALID` on authoritative refresh loss.
 * @throws NetworkError on transport failure, abort, or a transient refresh
 *   failure (the session is preserved).
 */
async function openAskStream(body: AgentV2AskRequest, signal: AbortSignal): Promise<Response> {
  const tokenAtStart = useAuthStore.getState().accessToken;
  let res = await postAsk(body, tokenAtStart, signal);

  if (res.status === 401 && tokenAtStart) {
    const tokenNow = useAuthStore.getState().accessToken;
    if (tokenNow !== tokenAtStart && tokenNow != null) {
      // Stale 401: a concurrent request already refreshed the store. Retry
      // with the rotated token WITHOUT burning a second refresh.
      res = await postAsk(body, tokenNow, signal);
    } else {
      try {
        const newToken = await coordinatedRefresh();
        res = await postAsk(body, newToken, signal);
      } catch (e) {
        // Transient (5xx / throttle / session-changed all subclass
        // NetworkError): preserve the session and surface a retryable error.
        if (e instanceof NetworkError) throw e;
        // Authoritative auth loss (4xx from /api/auth/refresh): converge
        // sister tabs to logged-out state, then surface AUTH_TOKEN_INVALID.
        const reason = e instanceof ApiClientError ? refreshErrorReason(e) : 'AUTH_REFRESH_4XX';
        clearWithBroadcast(reason);
        throw new ApiClientError(401, 'AUTH_TOKEN_INVALID', 'Session expired');
      }
    }
  }

  if (!res.ok) throw await parseApiError(res);
  if (!res.body) throw new NetworkError('Agent stream has no body');
  return res;
}

// WHY: SSE frames are separated by a blank line. We split the carried buffer
// on a blank line (handling both `\n\n` and `\r\n\r\n`); each frame is the
// `data:`-prefixed payload lines joined back together. Non-`data:` lines
// (e.g. `event:`, `:` comments) are ignored — this endpoint only emits data.
function extractDataPayload(frame: string): string | null {
  const lines = frame.split(/\r?\n/);
  const dataLines = lines
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''));
  if (dataLines.length === 0) return null;
  return dataLines.join('\n');
}

function parseFrame(frame: string): AgentV2Event | null {
  const payload = extractDataPayload(frame);
  if (payload === null || payload.length === 0) return null;
  return AgentV2EventSchema.parse(JSON.parse(payload));
}

/**
 * @aiPurpose async-iterable consumer for the agent SSE stream. Opens the
 *   POST stream (with one-shot 401 refresh), then reads the body via a
 *   `ReadableStream` reader, carrying a line buffer across chunks, splitting
 *   on blank lines, and parsing each `data:` frame through `AgentV2EventSchema`.
 *   Yields one typed `AgentV2Event` per frame.
 * @aiInvariant a stale client that receives an unknown event `type` fails
 *   loudly (the schema rejects) rather than silently dropping the event.
 * @throws ApiClientError / NetworkError per `openAskStream`.
 * @throws ZodError if a frame does not match `AgentV2EventSchema`.
 */
export async function* streamAgentAsk(
  body: AgentV2AskRequest,
  signal: AbortSignal,
): AsyncGenerator<AgentV2Event, void, void> {
  const res = await openAskStream(body, signal);
  // Non-null: openAskStream throws when `res.body` is absent.
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // WHY: cancel the reader when the caller aborts so a blocked `reader.read()`
  // (e.g. a slow server holding the connection open) unblocks promptly and the
  // loop tears down. Without this, abort only takes effect on the NEXT chunk,
  // stranding an unmount/new-conversation behind an idle stream.
  const onAbort = () => {
    void reader.cancel().catch(() => {});
  };
  if (signal.aborted) onAbort();
  signal.addEventListener('abort', onAbort);

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done || signal.aborted) break;
      buffer += decoder.decode(value, { stream: true });
      // Split on blank-line separators; the last element is the trailing
      // partial frame that stays buffered until its terminator arrives.
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        const event = parseFrame(frame);
        if (event) yield event;
      }
    }
    // Flush any final frame that lacked a trailing blank line.
    const tail = buffer.trim();
    if (tail.length > 0) {
      const event = parseFrame(tail);
      if (event) yield event;
    }
  } finally {
    signal.removeEventListener('abort', onAbort);
    // WHY: releasing the lock lets an aborted/garbage-collected stream tear
    // down cleanly; `cancel` is best-effort and may already be aborted.
    void reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
